import { CheckCircle, Circle, Clock } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

interface Challenge {
  id: number
  title: string
  description: string
  type: string
  xp_reward: number
  progress: number
  deadline: string | null
}

interface ChallengeListProps {
  challenges: Challenge[]
}

export default function ChallengeList({ challenges }: ChallengeListProps) {
  if (challenges.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <p>No active challenges right now</p>
        <p className="text-sm mt-1">Check back later for new challenges!</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {challenges.map((challenge) => (
        <ChallengeCard key={challenge.id} challenge={challenge} />
      ))}
    </div>
  )
}

function ChallengeCard({ challenge }: { challenge: Challenge }) {
  const isComplete = challenge.progress >= 100
  const typeColors: Record<string, string> = {
    daily: 'bg-blue-500/20 text-blue-400',
    weekly: 'bg-purple-500/20 text-purple-400',
    skill_recovery: 'bg-orange-500/20 text-orange-400',
    family: 'bg-green-500/20 text-green-400',
  }

  return (
    <div
      className={`challenge-card ${isComplete ? 'completed' : 'active'}`}
    >
      <div className="flex items-start gap-3">
        {/* Status icon */}
        <div className="mt-0.5">
          {isComplete ? (
            <CheckCircle className="w-5 h-5 text-green-500" />
          ) : (
            <Circle className="w-5 h-5 text-gray-500" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs px-2 py-0.5 rounded-full ${typeColors[challenge.type] || 'bg-gray-500/20 text-gray-400'}`}>
              {challenge.type}
            </span>
            <span className="text-sm font-medium text-quest-accent">
              +{challenge.xp_reward} XP
            </span>
          </div>

          <h3 className={`font-medium ${isComplete ? 'line-through text-gray-500' : ''}`}>
            {challenge.title}
          </h3>

          {challenge.description && (
            <p className="text-sm text-gray-400 mt-1 line-clamp-2">
              {challenge.description}
            </p>
          )}

          {/* Progress bar for partial completion */}
          {!isComplete && challenge.progress > 0 && (
            <div className="mt-2">
              <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-quest-primary"
                  style={{ width: `${challenge.progress}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {Math.round(challenge.progress)}% complete
              </p>
            </div>
          )}

          {/* Deadline */}
          {challenge.deadline && !isComplete && (
            <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              <span>
                Due {formatDistanceToNow(new Date(challenge.deadline), { addSuffix: true })}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
